import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { getMessages, sendMessage, markAsRead, Message as ApiMessage } from "@/api/messages";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  MessageCircle,
  Building2,
  User,
  Calendar,
  Send,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

// Definir una interfaz local para Message que coincida con lo que usamos en el componente
interface LocalMessage {
  _id: string;
  senderId: string;
  receiverId: string;
  propertyId: string;
  content: string;
  createdAt: string;
  read: boolean;
}

interface Conversation {
  participantId: string;
  propertyTitle: string;
  lastMessage: LocalMessage;
  unreadCount: number;
  messages: LocalMessage[];
}

export function MessagesPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const apiMessages = await getMessages();
        
        // Agrupar mensajes por propiedad y participante
        const conversationsMap = new Map<string, Conversation>();
        
        apiMessages.forEach((apiMessage: ApiMessage) => {
          // Convertir el mensaje de la API al formato local
          const message: LocalMessage = {
            _id: apiMessage._id,
            senderId: apiMessage.senderId._id,
            receiverId: apiMessage.receiverId._id,
            propertyId: apiMessage.propertyId._id,
            content: apiMessage.content,
            createdAt: apiMessage.createdAt,
            read: apiMessage.read
          };
          
          const participantId = message.senderId === user?.id ? message.receiverId : message.senderId;
          const propertyTitle = apiMessage.propertyId.title;
          const key = `${participantId}-${message.propertyId}`;
          
          if (!conversationsMap.has(key)) {
            conversationsMap.set(key, {
              participantId,
              propertyTitle,
              lastMessage: message,
              unreadCount: message.senderId !== user?.id && !message.read ? 1 : 0,
              messages: [message],
            });
          } else {
            const conversation = conversationsMap.get(key)!;
            conversation.messages.push(message);
            if (message.createdAt > conversation.lastMessage.createdAt) {
              conversation.lastMessage = message;
            }
            if (message.senderId !== user?.id && !message.read) {
              conversation.unreadCount++;
            }
          }
        });
        
        setConversations(Array.from(conversationsMap.values()));
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching messages:', error);
        toast.error('Error al cargar los mensajes');
      }
    };

    if (user) {
      fetchMessages();
    }
  }, [user]);

  const handleSendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;

    try {
      const apiMessage = await sendMessage({
        receiverId: selectedConversation.participantId,
        propertyId: selectedConversation.lastMessage.propertyId,
        content: newMessage,
      });

      // Convertir el mensaje de la API al formato local
      const message: LocalMessage = {
        _id: apiMessage._id,
        senderId: apiMessage.senderId._id,
        receiverId: apiMessage.receiverId._id,
        propertyId: apiMessage.propertyId._id,
        content: apiMessage.content,
        createdAt: apiMessage.createdAt,
        read: apiMessage.read
      };

      // Actualizar la conversación seleccionada
      setSelectedConversation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          lastMessage: message,
          messages: [...prev.messages, message],
        };
      });

      // Actualizar la lista de conversaciones
      setConversations(prev =>
        prev.map(conv =>
          conv.participantId === selectedConversation.participantId
            ? {
                ...conv,
                lastMessage: message,
                messages: [...conv.messages, message],
              }
            : conv
        )
      );

      setNewMessage("");
      toast.success('Mensaje enviado');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje');
    }
  };

  const handleSelectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);

    // Marcar mensajes como leídos
    if (conversation.unreadCount > 0) {
      try {
        const unreadMessages = conversation.messages
          .filter(m => m.senderId === conversation.participantId && !m.read)
          .map(m => m._id);

        await Promise.all(unreadMessages.map(messageId => markAsRead(messageId)));

        // Actualizar estado local
        setConversations(prev =>
          prev.map(conv =>
            conv.participantId === conversation.participantId
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.propertyTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Mensajes</h2>
            <p className="text-muted-foreground">
              Gestiona tus conversaciones con compradores y vendedores
            </p>
          </div>
        </div>

        {/* Stats Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Conversaciones
            </CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversations.length}</div>
          </CardContent>
        </Card>

        {/* Messages Interface */}
        <div className="grid grid-cols-12 gap-6">
          {/* Conversations List */}
          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>Conversaciones</CardTitle>
              <CardDescription>
                Busca y selecciona una conversación
              </CardDescription>
              <div className="relative">
                <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por propiedad..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  {filteredConversations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No hay conversaciones
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredConversations.map((conversation) => (
                        <div
                          key={`${conversation.participantId}-${conversation.lastMessage.propertyId}`}
                          className={cn(
                            "p-4 rounded-lg cursor-pointer hover:bg-accent",
                            selectedConversation?.participantId === conversation.participantId
                              ? "bg-accent"
                              : "bg-card"
                          )}
                          onClick={() => handleSelectConversation(conversation)}
                        >
                          <div className="flex items-start gap-4">
                            <Avatar>
                              <AvatarImage src="/placeholder-avatar.jpg" />
                              <AvatarFallback>
                                {conversation.propertyTitle[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium leading-none">
                                  {conversation.propertyTitle}
                                </h4>
                                {conversation.unreadCount > 0 && (
                                  <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                                    {conversation.unreadCount}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {conversation.lastMessage.content}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(conversation.lastMessage.createdAt), "d 'de' MMMM, HH:mm", { locale: es })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Messages View */}
          <Card className="col-span-8">
            <CardHeader>
              <CardTitle>
                {selectedConversation ? (
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {selectedConversation.propertyTitle}
                  </div>
                ) : (
                  "Selecciona una conversación"
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedConversation ? (
                <>
                  <ScrollArea className="h-[400px] mb-4">
                    <div className="space-y-4">
                      {selectedConversation.messages.map((message) => {
                        const isOwn = message.senderId === user?.id;
                        return (
                          <div
                            key={message._id}
                            className={cn(
                              "flex",
                              isOwn ? "justify-end" : "justify-start"
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-[70%] rounded-lg px-4 py-2",
                                isOwn
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted"
                              )}
                            >
                              <p>{message.content}</p>
                              <p className="text-xs opacity-70 mt-1">
                                {format(new Date(message.createdAt), "HH:mm", { locale: es })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Escribe un mensaje..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSendMessage();
                        }
                      }}
                    />
                    <Button onClick={handleSendMessage}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <MessageCircle className="h-12 w-12 mb-4" />
                  <p>Selecciona una conversación para ver los mensajes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
} 